/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { useMemo } from 'react'
import moment from 'moment'

import { EuiFlexGroup, EuiFlexItem, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useGetOrganizationQuery } from '@modules/profile-lib/hooks'
import {
  DEFAULT_TRIAL_DAYS,
  getSaasEntityTrialDaysRemaining,
  getSaasLatestTrial,
} from '@modules/profile-lib/trial'
import ExtendTrialButton from '@modules/trial-components/ExtendTrialButton'

import CreditCardModalButton from '@/components/Billing/CreditCard/CreditCardModalButton'

import SubscriptionOverviewPanel from '../components/SubscriptionOverviewPanel'

const ActiveOrNotStartedTrial = ({ organizationId }) => {
  const organizationQuery = useGetOrganizationQuery(organizationId)

  const trialDescription = useMemo(() => {
    const trialDaysRemaining =
      organizationQuery.data && getSaasEntityTrialDaysRemaining(organizationQuery.data)
    const trialEnd = organizationQuery.data && getSaasLatestTrial(organizationQuery.data)?.end

    if (trialDaysRemaining && trialEnd) {
      return (
        <EuiText size='s' data-test-id='trial-overview.active-trial.description'>
          <FormattedMessage
            id='trial-overview.active-trial.description'
            defaultMessage='You have <strong>{trialDaysRemaining, plural, one {{trialDaysRemaining} day} other {{trialDaysRemaining} days}}</strong> remaining on your trial, which will end on {endDate}.{br}To lift trial restrictions now and to be able to continue using Elastic Cloud, you can subscribe at any time.'
            values={{
              trialDaysRemaining,
              strong: (content) => <strong>{content}</strong>,
              endDate: moment(trialEnd).format('YYYY-MM-DD [at] HH:mm [UTC]'),
              br: <br />,
            }}
          />
        </EuiText>
      )
    }

    return (
      <EuiText size='s' data-test-id='trial-overview.not-started-trial.description'>
        <FormattedMessage
          id='trial-overview.not-started-trial.description'
          defaultMessage='Free <strong>{trialLength}-day</strong> trial. To lift trial restrictions now and to be able to continue using Elastic Cloud, you can subscribe at any time.'
          values={{
            trialLength: organizationQuery.data?.organization.trial_length || DEFAULT_TRIAL_DAYS,
            strong: (content) => <strong>{content}</strong>,
            br: <br />,
          }}
        />
      </EuiText>
    )
  }, [organizationQuery.data])

  return (
    <SubscriptionOverviewPanel
      title={<FormattedMessage id='trial-overview.active-trial.title' defaultMessage='Trial' />}
      content={trialDescription}
      footer={
        <EuiFlexGroup>
          <EuiFlexItem grow={false}>
            <CreditCardModalButton type='full'>
              <FormattedMessage id='trial-overview.subscribe-button' defaultMessage='Subscribe' />
            </CreditCardModalButton>
          </EuiFlexItem>
          <ExtendTrialButton
            type='link'
            source_component_id='expiring_billing_panel'
            buttonProps={{ style: { fontWeight: 'bold' } }}
          >
            <FormattedMessage
              id='trial-overview.trial-expiring-extend'
              defaultMessage='Extend your trial'
            />
          </ExtendTrialButton>
        </EuiFlexGroup>
      }
    />
  )
}

export default ActiveOrNotStartedTrial
